import type { PageNoteView, Verdict } from "@anota/shared";
import type { ComponentChildren } from "preact";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { NotesApi } from "../backend";
import type { Strings } from "../i18n";
import { arePinsHidden, setPinsHidden } from "../storage";
import { ago } from "./time";

/**
 * Las notas de esta página para quien la revisa (con enlace de revisión): un pin numerado del color de su
 * estado sobre lo que se señaló, una lista con todas (también las que ya no se encuentran) y, al abrir
 * una, la conversación con el equipo y un cuadro para responder.
 */

type Note = PageNoteView;
type Spot = { x: number; y: number };

/** El elemento señalado, si sigue en la página: por su selector o, si no, por su XPath. */
export function findElement(a: NonNullable<Note["anchor"]>): Element | null {
  let el: Element | null = null;
  try {
    el = document.querySelector(a.selector);
  } catch {
    el = null;
  }
  if (!el && a.xpath) {
    try {
      el = document.evaluate(a.xpath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue as Element | null;
    } catch {
      el = null;
    }
  }
  return el;
}

/** Dónde está ahora lo señalado: el elemento y el punto marcado dentro. */
function locate(n: Note): Spot | null {
  const el = n.anchor && findElement(n.anchor);
  if (!el) return null;
  const a = n.anchor!;
  const r = el.getBoundingClientRect();
  if (!r.width && !r.height) return null;
  const p = a.point ?? { x: 0, y: 0 };
  return { x: r.left + p.x * r.width, y: r.top + p.y * r.height };
}

/** Vuelve a pintar al hacer scroll o cambiar el tamaño: los pines siguen a lo señalado. */
export function useReposition() {
  const [, setTick] = useState(0);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setTick((n) => n + 1));
    };
    addEventListener("scroll", update, true);
    addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(frame);
      removeEventListener("scroll", update, true);
      removeEventListener("resize", update);
    };
  }, []);
}

/** «¿Está bien así?» en una nota resuelta de quien mira: darla por buena o reabrirla diciendo qué sigue mal. */
function VerdictBox({ note, t, send, onDone }: { note: Note; t: Strings; send: (id: string, verdict: Verdict, body?: string) => Promise<void>; onDone: () => void }) {
  const [asking, setAsking] = useState(false);
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (asking) area.current?.focus();
  }, [asking]);
  if (note.confirmedAt)
    return (
      <p class="np-ok">
        <span aria-hidden="true">✓</span> {t.confirmed}
      </p>
    );

  async function go(verdict: Verdict, body: string) {
    setBusy(true);
    setError(false);
    try {
      await send(note.id, verdict, body);
      onDone();
    } catch {
      setError(true);
      setBusy(false);
    }
  }

  return (
    <div class="np-verdict" role="group" aria-label={t.verdictAsk}>
      <b>{t.verdictAsk}</b>
      {asking ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (why.trim()) void go("reopen", why.trim());
          }}
        >
          <textarea
            ref={area}
            rows={2}
            value={why}
            maxLength={5000}
            placeholder={t.verdictWhy}
            aria-label={t.verdictWhy}
            disabled={busy}
            onInput={(e) => setWhy((e.currentTarget as HTMLTextAreaElement).value)}
          />
          <span class="np-verdict-row">
            <button type="submit" class="btn primary" disabled={busy || !why.trim()}>
              {t.verdictSend}
            </button>
            <button type="button" class="btn" disabled={busy} onClick={() => setAsking(false)}>
              {t.cancel}
            </button>
          </span>
        </form>
      ) : (
        <span class="np-verdict-row">
          <button type="button" class="btn primary" disabled={busy} onClick={() => void go("ok", t.verdictOkBody)}>
            {t.verdictOk}
          </button>
          <button type="button" class="btn" disabled={busy} onClick={() => setAsking(true)}>
            {t.verdictNo}
          </button>
        </span>
      )}
      {error && (
        <p class="err" role="alert">
          {t.error}
        </p>
      )}
    </div>
  );
}

/**
 * Lo que dice la nota, lo que se hizo y la conversación, con un cuadro para responder (pines y «Tus notas»).
 * Con `mine`, la nota es de quien mira: sus respuestas salen como «Tú». Con `verdict`, en sus notas resueltas
 * pregunta «¿Está bien así?».
 */
export function Conversation({
  note,
  t,
  reply,
  verdict,
  onReplied,
  mine,
  children,
}: {
  note: Note;
  t: Strings;
  reply: (id: string, body: string) => Promise<void>;
  verdict?: (id: string, verdict: Verdict, body?: string) => Promise<void>;
  onReplied: () => void;
  mine?: boolean;
  /** Lo que va entre la nota y lo que se hizo («Tus notas»: dónde está y en qué punto va). */
  children?: ComponentChildren;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);

  /** El cuadro crece con el texto (hasta un tope, por CSS) y vuelve a una línea al enviar. */
  const grow = () => {
    const el = area.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  };

  async function send(e: Event) {
    e.preventDefault();
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    try {
      await reply(note.id, body);
      setText("");
      if (area.current) area.current.style.height = "";
      onReplied();
      // Sigue en la conversación (el botón se desactiva al vaciarse el cuadro y perdería el foco).
      area.current?.focus();
    } catch {
      setError(t.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p class="np-desc">{note.description}</p>
      {note.reporterName && !mine && <p class="np-meta">{note.reporterName}</p>}
      {children}
      {note.resolution && (
        <p class="np-done">
          <b>{t.done}</b> {note.resolution}
        </p>
      )}
      {verdict && note.mine && note.status === "resolved" && <VerdictBox key={note.id} note={note} t={t} send={verdict} onDone={onReplied} />}
      {note.comments.length > 0 && (
        // Como un chat: lo del cliente a la derecha; lo del equipo y los agentes, a la izquierda.
        <ol class="np-thread">
          {note.comments.map((c, i) => {
            const fromTeam = c.authorType === "member" && Boolean(c.authorName);
            const who = c.authorType === "reporter" ? (mine ? t.you : (c.authorName ?? "")) : c.authorType === "agent" ? t.agent : (c.authorName ?? t.team);
            return (
              <li key={i} class={`by-${c.authorType}`}>
                <span class="np-who">
                  <b>{who}</b>
                  {fromTeam && <span class="np-tag">{t.team}</span>}
                  <time dateTime={c.createdAt}>{ago(c.createdAt, t.lang)}</time>
                </span>
                <span class="np-body">{c.body}</span>
              </li>
            );
          })}
        </ol>
      )}
      <form class="np-reply" onSubmit={send}>
        <textarea
          ref={area}
          rows={1}
          value={text}
          maxLength={5000}
          placeholder={t.replyPlaceholder}
          aria-label={t.replyPlaceholder}
          onInput={(e) => {
            setText((e.currentTarget as HTMLTextAreaElement).value);
            grow();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) void send(e);
          }}
          disabled={busy}
        />
        <button type="submit" class="send" aria-label={t.reply} title={`${t.reply} · Ctrl + Enter`} disabled={busy || !text.trim()}>
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3.4 20.4 21 12 3.4 3.6 3.4 10l12.6 2-12.6 2z" fill="currentColor" />
          </svg>
        </button>
        {error && (
          <p class="err" role="alert">
            {error}
          </p>
        )}
      </form>
    </>
  );
}

function Thread({ note, t, api, onReplied, onClose, at }: { note: Note; t: Strings; api: NotesApi; onReplied: () => void; onClose: () => void; at: Spot | null }) {
  const close = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => close.current?.focus(), [note.id]);
  // Junto al pin si se ve; si no (o en el móvil, por CSS), abajo.
  const style = at ? { left: `${Math.max(8, Math.min(at.x + 20, innerWidth - 328))}px`, top: `${Math.max(8, Math.min(at.y - 12, innerHeight * 0.45))}px` } : undefined;
  return (
    <div
      class={at ? "npop" : "npop docked"}
      style={style}
      role="dialog"
      aria-label={t.noteN(note.number)}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div class="np-head">
        <span class={`np-status s-${note.status}`}>{t.status[note.status]}</span>
        <b>{t.noteN(note.number)}</b>
        <button ref={close} type="button" class="x" aria-label={t.close} title={t.close} onClick={onClose}>
          ✕
        </button>
      </div>
      <Conversation key={note.id} note={note} t={t} reply={api.reply} verdict={api.verdict} onReplied={onReplied} />
    </div>
  );
}

export function Pins({ api, t, refresh }: { api: NotesApi; t: Strings; refresh: number }) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  // Quién abrió la nota (para devolverle el foco al cerrarla).
  const opener = useRef<HTMLElement | null>(null);
  const [list, setList] = useState(false);
  const [hidden, setHidden] = useState(arePinsHidden);
  useReposition();

  const load = useCallback(() => {
    api.list(location.href).then(setNotes, () => setNotes([]));
  }, [api]);

  useEffect(() => load(), [load, refresh]);

  // Webs de una sola página: al cambiar de ruta, las notas de la nueva. Y al volver a la pestaña, lo nuevo.
  useEffect(() => {
    let path = location.pathname;
    const check = () => {
      if (location.pathname === path) return;
      path = location.pathname;
      setOpen(null);
      load();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    const id = setInterval(check, 1000);
    addEventListener("popstate", check);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      removeEventListener("popstate", check);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  // Esc cierra la conversación o la lista, esté donde esté el foco.
  useEffect(() => {
    if (!open && !list) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(null);
      setList(false);
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [open, list]);

  if (!notes.length) return null;
  // Varias notas sobre lo mismo: los pines se apartan un poco para que se vean y se puedan abrir todos.
  const used: Spot[] = [];
  const placed = notes.map((n) => {
    let at = locate(n);
    for (let i = 0; at && i < 20 && used.some((u) => Math.abs(u.x - at!.x) < 30 && Math.abs(u.y - at!.y) < 30); i++) at = { x: at.x + 32, y: at.y };
    if (at) used.push(at);
    return { n, at };
  });
  const current = placed.find((p) => p.n.id === open);

  return (
    <>
      {!hidden &&
        placed.map(({ n, at }, i) =>
          at ? (
            <button
              key={n.id}
              type="button"
              class={`npin s-${n.status}${open === n.id ? " on" : ""}`}
              style={{ left: `${at.x}px`, top: `${at.y}px`, "--i": Math.min(i, 10) }}
              aria-label={`${t.noteN(n.number)} · ${t.status[n.status]}`}
              title={`${t.noteN(n.number)} · ${t.status[n.status]}`}
              onClick={(e) => {
                opener.current = e.currentTarget;
                setOpen(open === n.id ? null : n.id);
              }}
            >
              {n.number}
            </button>
          ) : null,
        )}

      <button
        type="button"
        class="notes-chip"
        aria-expanded={list}
        onClick={() => {
          setOpen(null);
          setList(!list);
        }}
      >
        {t.notesHere(notes.length)}
      </button>
      {list && (
        <div class="notes-list" role="dialog" aria-label={t.notesTitle}>
          <div class="nl-head">
            <b>{t.notesTitle}</b>
            <button
              type="button"
              class="link"
              onClick={() => {
                setPinsHidden(!hidden);
                setHidden(!hidden);
              }}
            >
              {hidden ? t.showPins : t.hidePins}
            </button>
          </div>
          <ul>
            {placed.map(({ n, at }) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => {
                    setList(false);
                    setOpen(n.id);
                    // Lo señalado, a la vista (si sigue en la página).
                    if (at && (at.y < 0 || at.y > innerHeight))
                      scrollBy({
                        top: at.y - innerHeight / 3,
                        behavior: "smooth",
                      });
                  }}
                >
                  <span class={`np-status s-${n.status}`}>{t.status[n.status]}</span>
                  <span class="nl-num">#{n.number}</span>
                  <span class="nl-desc">{n.description}</span>
                  {!at && <span class="nl-where">{n.anchor ? t.notFound : t.wholePageShort}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {current && <Thread note={current.n} t={t} api={api} at={hidden ? null : current.at} onReplied={load} onClose={() => {
            setOpen(null);
            requestAnimationFrame(() => opener.current?.isConnected && opener.current.focus());
          }} />}
    </>
  );
}
